import React, { Fragment, useEffect } from 'react';
import { compose, map, pipe, values } from 'ramda';
import Form from '@core/js/components/common/Form';
import { ErrorMessage } from '@core/js/components/common/ErrorMessage';
import DateSpanPicker from '@core/js/components/common/Fields/DateSpanPicker';
import EditableDropDown from '@core/js/components/common/Fields/EditableDropDown';
import { EDITABLE_DD_MODES } from '@core/js/components/common/Fields/EditableDropDown/constants';

import Hint from '@core/js/components/main/ResponsibleGaming/Hint';
import { PendingLimits } from '@core/js/components/main/ResponsibleGaming/PendingLimits';
import { LIMIT_TYPES, LIMITS_TYPE, UNLIMITED } from '@core/js/components/main/ResponsibleGaming/LoginLimits/constants';
import { parseLimits, parsePendingLimits } from '@core/js/components/main/ResponsibleGaming/LoginLimits/helpers';
import { useGetPendingLimitText } from '@core/js/components/main/ResponsibleGaming/LoginLimits/hooks';

import { Provider } from 'react-redux';
import { store } from '@core/js/store';
import { useTranslations } from '@core/js/hooks/i18n';
import { dispatcher } from 'dispatcher';
import { useValidationConfig } from './hooks';
import { withLoginConfig } from './hocs';
import { formatData } from './helpers';

import style from './index.scss';

const LoginLimitsBare = (props) => {
    let formSubmit = null;
    const { config, limits, pendingLimits, initialValues } = props;

    const validationConfig = useValidationConfig(config);
    const t = useTranslations('playtech.mobile.system.loginLimits.');
    const getPendingLimitText = useGetPendingLimitText();

    const onSubmit = () => ({ result: true });

    const { noLimitsAllowed = true } = config;
    const { monthlyLoginCount, ...loginTimeLimits } = parseLimits(limits, config);
    const parsedLimits = parseLimits(limits, config);
    const parsedPendingLimits = parsePendingLimits(pendingLimits).map(item => ({
        ...item,
        text: getPendingLimitText(item)
    }));

    useEffect(() => {
        dispatcher.listen('regLoginLimits:validate', async (handler) => {
            const result = await formSubmit();
            handler(Boolean(result));
        });

        return () => dispatcher.removeListener('regLoginLimits:validate');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Form
            onSubmit={onSubmit}
            formatData={formatData}
            initialValues={initialValues}
            validationConfig={validationConfig}
            validateOnBlur
            render={({ submitError, handleSubmit, values: formValues }) => {
                formSubmit = handleSubmit;
                return (
                    <>
                    <form
                        className={style.form}
                        onSubmit={event => event.preventDefault()}
                    >
                        <section className={style.loginLimits}>
                            {submitError && <ErrorMessage error={submitError} animate={true}/>}
                            {config.monthlyLoginCount && (
                                <Fragment>
                                    <p className={style.description}>
                                        {t('limitsDescription')}
                                    </p>
                                    <div className={style.fieldSet}>
                                        <EditableDropDown
                                            classNames={style}
                                            dataList={[UNLIMITED]}
                                            className={style.control}
                                            name={monthlyLoginCount.name}
                                            label={t(monthlyLoginCount.label)}
                                            placeholder={t(monthlyLoginCount.placeholder)}
                                            displayValuesMap={{ [UNLIMITED]: t('date.unlimited') }}
                                            mode={noLimitsAllowed ? EDITABLE_DD_MODES.ALL : EDITABLE_DD_MODES.TEXT_ONLY}
                                        />
                                    </div>
                                </Fragment>
                            )}

                            {(config.dailyLoginTime || config.weeklyLoginTime || config.monthlyLoginTime) && (
                                <Fragment>
                                    <div className={style.fieldSet}>
                                        {Object.entries(loginTimeLimits)
                                            .filter(([, { allowed }]) => allowed)
                                            .map(([key, { limitType, name, label, placeholder, resetDate }]) =>
                                                <DateSpanPicker
                                                    key={key}
                                                    name={name}
                                                    label={t(label)}
                                                    title={t(placeholder)}
                                                    unlimitedValue={UNLIMITED}
                                                    allowUnlimited={noLimitsAllowed}
                                                    hint={<Hint date={resetDate}/>}
                                                    classNames={style}
                                                    useDays={limitType !== LIMIT_TYPES.DAILY_LIMIT}
                                                    maxDays={limitType === LIMIT_TYPES.WEEKLY_LIMIT ? 7 : 25}
                                                    maxHours={limitType === LIMIT_TYPES.DAILY_LIMIT ? 18 : undefined}
                                                />)
                                        }
                                    </div>
                                </Fragment>
                            )}
                        </section>

                        {parsedPendingLimits.length > 0 && (
                            <section className={style.appliedLimits}>
                                <h2 className={style.title}>{t('pendingLimitsTitle')}</h2>
                                <PendingLimits
                                    classNames={style}
                                    limitType={LIMITS_TYPE}
                                    limits={parsedPendingLimits}
                                />
                            </section>
                        )}
                    </form>
                        {pipe(
                            values,
                            map(({ allowed, limitType, name }) =>
                                allowed && limitType !== LIMIT_TYPES.LOGIN_COUNT &&
                                <Fragment key={name}>
                                    <div className="fn-validate">
                                        <input
                                            type="hidden"
                                            name={name}
                                            value={formValues[name]}
                                        />
                                    </div>
                                </Fragment>
                            )
                        )(parsedLimits)}
                    </>
                );
            }}
        />
    );
};

const LoginLimitsWrapped = compose(
    withLoginConfig()
)(LoginLimitsBare);

export const LoginLimitsForm = () => (
    <Provider store={store}>
        <LoginLimitsWrapped/>
    </Provider>
);
